/**
 * --------------------------------------------------------
 * VDB Framework
 * The Automotive Intelligence Layer for Shopify
 *
 * Module  : Vehicle Core
 * Version : 1.0.0
 * Build   : 003
 * Commit  : 003.2
 * Schema  : Vehicle v2
 * --------------------------------------------------------
 */
(function (window, document) {
  "use strict";

  const C = window.VDBConfig || {};
  const VDB = window.VDB = window.VDB || {};

  VDB.version = C.version || "1.0.0";
  VDB.build = C.build || "003";
  VDB.commit = C.commit || "003.2";
  VDB.vehicleSchemaVersion = 2;
  VDB.ui = VDB.ui || {};
  VDB.utils = VDB.utils || {};

  let current = null;

  function asString(value) {
    return value === undefined || value === null ? "" : String(value).trim();
  }

  function asNumber(value) {
    if (value === undefined || value === null || value === "") return null;
    const number = Number(value);
    return Number.isFinite(number) ? number : null;
  }

  function clone(value) {
    return value === null || value === undefined ? null : JSON.parse(JSON.stringify(value));
  }

  function getPath(object, path) {
    return path.split(".").reduce(function (value, key) {
      return value && value[key] !== undefined ? value[key] : null;
    }, object);
  }

  function createDisplayName(vehicle) {
    return [
      vehicle.production.year,
      vehicle.make.name,
      vehicle.model.name,
      vehicle.engine.description,
      vehicle.version.name || vehicle.body.style
    ].filter(Boolean).join(" ");
  }

  function normalise(input) {
    if (!input || typeof input !== "object") return null;

    const vehicle = {
      schemaVersion: 2,
      versionId: asString(input.versionId || input.vdb_version_id),
      versionHandle: asString(input.versionHandle || input.version_handle),
      make: {
        id: asString(input.make && typeof input.make === "object" ? input.make.id : input.makeId || input.make_code),
        name: asString(input.make && typeof input.make === "object" ? input.make.name : input.makeName || input.make)
      },
      model: {
        id: asString(input.model && typeof input.model === "object" ? input.model.id : input.modelId || input.model_code),
        name: asString(input.model && typeof input.model === "object" ? input.model.name : input.modelName || input.model)
      },
      version: {
        name: asString(input.version && typeof input.version === "object" ? input.version.name : input.versionName || input.variant)
      },
      engine: {
        code: asString(input.engine && typeof input.engine === "object" ? input.engine.code : input.engineCode || input.engine_code),
        description: asString(input.engine && typeof input.engine === "object" ? input.engine.description : input.engineDescription || input.engine),
        cc: asNumber(input.engine && typeof input.engine === "object" ? input.engine.cc : (input.engineCc !== undefined ? input.engineCc : input.engine_cc)),
        kw: asNumber(input.engine && typeof input.engine === "object" ? input.engine.kw : input.kw),
        hp: asNumber(input.engine && typeof input.engine === "object" ? input.engine.hp : input.hp)
      },
      production: {
        from: asString(input.production && typeof input.production === "object" ? input.production.from : input.yearFrom || input.year_from),
        to: asString(input.production && typeof input.production === "object" ? input.production.to : input.yearTo || input.year_to),
        year: asNumber(input.production && typeof input.production === "object" ? input.production.year : input.year)
      },
      body: {
        style: asString(input.body && typeof input.body === "object" ? input.body.style : input.bodyStyle || input.body_style),
        doors: asNumber(input.body && typeof input.body === "object" ? input.body.doors : input.doors)
      },
      fuel: asString(input.fuel),
      transmission: asString(input.transmission),
      drive: asString(input.drive || input.driveType || input.drive_type),
      displayName: asString(input.displayName || input.display_name),
      savedAt: asString(input.savedAt || input.saved_at)
    };

    if (!vehicle.displayName) vehicle.displayName = createDisplayName(vehicle);
    if (!vehicle.savedAt) vehicle.savedAt = new Date().toISOString();
    return vehicle;
  }

  function validate(input) {
    const vehicle = normalise(input);
    const required = ["versionId", "make.name", "model.name", "displayName"];
    const errors = !vehicle ? ["Vehicle is missing."] : required.filter(function (path) {
      return !getPath(vehicle, path);
    }).map(function (path) {
      return "Missing required vehicle field: " + path;
    });
    return { valid: errors.length === 0, errors: errors, vehicle: vehicle };
  }

  function emitError(source, error, details) {
    if (VDB.events && VDB.eventNames && VDB.eventNames.ERROR) {
      VDB.events.emit(VDB.eventNames.ERROR, { source: source, error: error, details: details || null });
    }
  }

  VDB.log = function () {
    if (!C.debug || !window.console) return;
    const args = Array.prototype.slice.call(arguments);
    args.unshift("[VDB]");
    window.console.log.apply(window.console, args);
  };

  VDB.info = function () {
    return {
      framework: "VDB Framework",
      tagline: "The Automotive Intelligence Layer for Shopify",
      version: VDB.version,
      build: VDB.build,
      commit: VDB.commit,
      vehicleSchema: VDB.vehicleSchemaVersion,
      storageSchema: VDB.storage && VDB.storage.schemaVersion ? VDB.storage.schemaVersion : null
    };
  };

  VDB.vehicle = {
    schemaVersion: 2,
    normalise: normalise,
    validate: validate,
    get: function () { return clone(current); },
    exists: function () { return Boolean(current); },
    set: function (input) {
      const result = validate(input);
      if (!result.valid) {
        const error = new Error(result.errors.join(" "));
        emitError("vehicle.set", error, result.errors);
        throw error;
      }
      const previous = clone(current);
      current = result.vehicle;
      current.savedAt = new Date().toISOString();
      if (!VDB.storage || !VDB.storage.saveVehicle(current)) {
        current = previous;
        const error = new Error("The vehicle could not be saved.");
        emitError("vehicle.set", error);
        throw error;
      }
      if (VDB.events && VDB.eventNames) {
        VDB.events.emit(previous ? VDB.eventNames.VEHICLE_CHANGED : VDB.eventNames.VEHICLE_SELECTED, { vehicle: this.get(), previous: previous });
      }
      VDB.log("Vehicle saved", this.get());
      return this.get();
    },
    save: function (input) { return this.set(input); },
    clear: function () {
      const previous = clone(current);
      current = null;
      if (VDB.storage) VDB.storage.clearVehicle();
      if (VDB.events && VDB.eventNames) VDB.events.emit(VDB.eventNames.VEHICLE_CLEARED, { previous: previous });
      VDB.log("Vehicle cleared");
    },
    reload: function () {
      if (!VDB.storage) { current = null; return null; }
      const stored = VDB.storage.loadVehicle();
      if (!stored) { current = null; return null; }
      const result = validate(stored);
      if (!result.valid) {
        current = null;
        VDB.storage.clearVehicle();
        emitError("vehicle.reload", new Error("Stored vehicle failed validation."), result.errors);
        return null;
      }
      current = result.vehicle;
      VDB.storage.saveVehicle(current);
      VDB.log("Vehicle loaded", this.get());
      return this.get();
    },
    summary: function () {
      if (!current) return "";
      return [current.production.year, current.make.name, current.model.name, current.engine.description, current.version.name || current.body.style].filter(Boolean).join(" ");
    },
    serialize: function () { return current ? JSON.stringify(current) : null; },
    toJSON: function () { return this.get(); }
  };

  VDB.health = function () {
    const modules = {
      events: Boolean(VDB.events),
      storage: Boolean(VDB.storage),
      api: Boolean(VDB.api),
      vehicle: Boolean(VDB.vehicle),
      lookup: Boolean(VDB.ui && VDB.ui.lookup),
      garage: Boolean(VDB.ui && VDB.ui.garage),
      drawer: Boolean(VDB.ui && VDB.ui.drawer)
    };
    return {
      ok: modules.events && modules.storage && modules.api && modules.vehicle,
      version: VDB.version,
      build: VDB.build,
      commit: VDB.commit,
      vehicleSchema: VDB.vehicleSchemaVersion,
      storageSchema: VDB.storage && VDB.storage.schemaVersion ? VDB.storage.schemaVersion : null,
      modules: modules,
      vehicleSelected: VDB.vehicle.exists(),
      vehicle: VDB.vehicle.get()
    };
  };

  VDB.utils.escapeHtml = function (value) {
    const div = document.createElement("div");
    div.textContent = value === null || value === undefined ? "" : String(value);
    return div.innerHTML;
  };

  function init() {
    VDB.vehicle.reload();
    document.documentElement.classList.add("vdb-ready");
    if (VDB.events && VDB.eventNames) {
      VDB.events.emit(VDB.eventNames.READY, { version: VDB.version, build: VDB.build, commit: VDB.commit, vehicle: VDB.vehicle.get() });
    }
    VDB.log("Framework ready", VDB.health());
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init, { once: true });
  else init();
})(window, document);
